import { useCallback, useEffect, useMemo } from 'react';
import { MarketStatus, TradeStatus } from '@sportspredict/shared';
import { EventCompetitionType, MARKET_SECTIONS } from '@/constants';
import {
	cloneObject,
	expandSelectionToGroup,
	findEarliestOpeningAndLatestClosing,
	getSlateMarketsCount,
} from '@/utils';

export function usePclSlateFiltering({
	slatesData,
	data,
	isSlatesLoading,
	slates,
	eventResponse,
	markets,
	userTrades,
	activeMarketSection,
	lobbyResponse,
	user,
	filteredByRuleSlates,
	setFilteredByRuleSlates,
	slateFiltersState,
	setSlateFiltersState,
}: any) {
	useEffect(() => {
		if (!data.lobbyResponse || isSlatesLoading) {
			return;
		}

		const contentRules = data.lobbyResponse?.content_rules;
		const incRule = contentRules?.include;
		const incSlateIds = incRule?.slate || [];
		const incMatchIds = incRule?.match || [];

		const newFilteredSlates: any[] = [];

		slates.forEach((s: any) => {
			const { id, name, closing_date, opening_date, matches, order, group_name } = s;

			const filteredMatchesByRule = matches.filter((m: any) => incMatchIds.includes(m.id));

			if (incMatchIds.length && filteredMatchesByRule.length === 0 && !incSlateIds.includes(id)) {
				return;
			}

			if (incSlateIds.length && !incSlateIds.includes(id) && !filteredMatchesByRule.length) {
				return;
			}

			const newMatches = incMatchIds.length ? filteredMatchesByRule : [...matches];

			const slData: any = {
				id,
				name,
				opening_date,
				closing_date,
				order,
				group_name,
				matches: newMatches,
			};

			if (!closing_date || !opening_date) {
				const { earliestOpening, latestClosing } = findEarliestOpeningAndLatestClosing(s);
				slData.opening_date = earliestOpening;
				slData.closing_date = latestClosing;
			}

			newFilteredSlates.push(slData);
		});

		setFilteredByRuleSlates(newFilteredSlates);
	}, [slates, lobbyResponse, data.lobbyResponse, isSlatesLoading]);

	// PLC EVENT TYPE SLATE TABS LOGIC
	const applyPCLAutoSlateSelection = useCallback(() => {
		if (!filteredByRuleSlates?.length) return;
		if (eventResponse?.competition_type !== EventCompetitionType) return;
		if (!markets) return;

		const hasAvailableMarketInSlate = (slate: any) => {
			if (!slate?.matches?.length) return false;

			const matchIds = new Set(slate.matches.map((m: any) => m.id));

			return markets.some(
				(market: any) =>
					market.status === MarketStatus.Open &&
					matchIds.has(market.match_id) &&
					!userTrades?.some((t: any) => t.market_id === market.id)
			);
		};

		const slatesOrdered = [...filteredByRuleSlates].sort(
			(a, b) => (a?.order ?? 0) - (b?.order ?? 0)
		);

		setSlateFiltersState((prev: any) => {
			const draft = cloneObject(prev) || {
				tabs: {
					[MARKET_SECTIONS.Active]: {},
					[MARKET_SECTIONS.Leaderboard]: {},
				},
			};

			const activeTabState = draft.tabs[MARKET_SECTIONS.Active] || {};
			let currentActive = activeTabState.currentSlate;

			if (!currentActive) {
				currentActive = slatesOrdered[0];
			}

			if (!currentActive || hasAvailableMarketInSlate(currentActive)) {
				return prev;
			}

			const currentIndex = slatesOrdered.findIndex((s) => s.id === currentActive.id);
			const rotated =
				currentIndex >= 0
					? [...slatesOrdered.slice(currentIndex + 1), ...slatesOrdered.slice(0, currentIndex)]
					: slatesOrdered;

			const nextSlate = rotated.find(hasAvailableMarketInSlate);
			if (!nextSlate) {
				return prev;
			}

			if (currentActive.id === nextSlate.id) return prev;

			[MARKET_SECTIONS.Active, MARKET_SECTIONS.Leaderboard].forEach((section) => {
				const tabState = draft.tabs[section] || (draft.tabs[section] = {});

				tabState.currentSlate = nextSlate;
				tabState.selected = tabState.selected || { slates: [], matches: [] };

				tabState.selected.slates = [nextSlate.id];
				tabState.selected.matches = nextSlate.matches?.map((m: any) => m.id) || [];
			});

			return draft;
		});
	}, [filteredByRuleSlates, eventResponse?.competition_type, markets, userTrades]);

	useEffect(() => {
		if (!filteredByRuleSlates || filteredByRuleSlates.length === 0) {
			return;
		}

		let lastUserTrade: any;
		let firstUpcomingSlate: any;
		const allMatchesWithOpenMarketIds: any[] = [];
		const allMatchesWithUserTradedMarketIds: any[] = [];
		const now = new Date();

		if (markets.length) {
			markets.forEach((market: any) => {
				if (
					market?.status === MarketStatus.Open &&
					!allMatchesWithOpenMarketIds.includes(market.match_id)
				) {
					allMatchesWithOpenMarketIds.push(market.match_id);
				}

				const userTrade = userTrades?.find((t: any) => t.market_id === market.id);
				if (userTrade) {
					if (!allMatchesWithUserTradedMarketIds.includes(market.match_id)) {
						allMatchesWithUserTradedMarketIds.push(market.match_id);
					}

					const userTradeCreated = new Date(userTrade.created_date);

					if (!lastUserTrade || lastUserTrade.date < userTradeCreated) {
						lastUserTrade = {
							id: userTrade.id,
							matchId: market.match_id,
							date: userTradeCreated,
						};
					}
				}
			});
		}

		// FOR PCL TYPE ONLY
		// helper: has user traded in all open markets for a given slate?
		const userTradedAllOpenMarketsForSlate = (slate: any) => {
			if (!slate?.matches?.length) return false;

			const matchIds = slate.matches.map((m: any) => m.id);

			// matches in this slate that currently have open markets
			const openMatchIdsInSlate = matchIds.filter((id: any) =>
				allMatchesWithOpenMarketIds.includes(id)
			);

			// if there are no open markets in this slate – don't treat as "all traded"
			if (!openMatchIdsInSlate.length) return false;

			// user must have traded in every match that has an open market
			return openMatchIdsInSlate.every((id: any) => allMatchesWithUserTradedMarketIds.includes(id));
		};

		const newSlateFiltersState = cloneObject(slateFiltersState);

		[MARKET_SECTIONS.MyPicks, MARKET_SECTIONS.Active, MARKET_SECTIONS.Leaderboard].forEach(
			(section) => {
				newSlateFiltersState.tabs[section].all = {
					slates: [],
				};
			}
		);

		filteredByRuleSlates.forEach((s: any) => {
			const { id, closing_date, opening_date, matches } = s;
			const matchesIds = matches.map((m: any) => m.id);

			const leaderboardFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.Leaderboard];
			const myPicksFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.MyPicks];
			const activeFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.Active];
			const userTradedAllOpenMarkets = userTradedAllOpenMarketsForSlate(s);

			leaderboardFilters.all.slates.push(cloneObject(s));

			if (matches.some((m: any) => allMatchesWithUserTradedMarketIds.includes(m.id))) {
				myPicksFilters.all.slates.push(cloneObject(s));

				if (lastUserTrade && matchesIds.includes(lastUserTrade.matchId)) {
					myPicksFilters.currentSlate = s;
				}

				if (
					lastUserTrade &&
					matchesIds.includes(lastUserTrade.matchId) &&
					myPicksFilters.isDefaultState
				) {
					myPicksFilters.selected.slates = [id];
					myPicksFilters.selected.matches = s.matches.map((m: any) => m.id);
				}
			}

			const hasOpenMarkets = matches.some((m: any) => allMatchesWithOpenMarketIds.includes(m.id));
			const openAt = new Date(opening_date);
			const closeAt = new Date(closing_date);

			const isActiveByTime = now >= openAt && now <= closeAt;
			const isWithin24hAfterClose =
				now > closeAt && now.getTime() - closeAt.getTime() <= 24 * 60 * 60 * 1000;

			// Show in Active filter if: has open markets OR (within time window) OR (within 24h after close)
			if (hasOpenMarkets || isActiveByTime || isWithin24hAfterClose) {
				activeFilters.all.slates.push(cloneObject(s));
			}

			// Keep default/current slate selection logic prioritizing slates that are active *and* have open markets
			if (
				hasOpenMarkets &&
				isActiveByTime &&
				!userTradedAllOpenMarkets && // PCL TYPE LOGIC
				(!activeFilters.currentSlate || s.order < activeFilters.currentSlate.order) &&
				!slateFiltersState?.isInited
			) {
				activeFilters.currentSlate = s;
				activeFilters.selected.slates = [id];
				activeFilters.selected.matches = s.matches.map((m: any) => m.id);

				leaderboardFilters.currentSlate = s;
				leaderboardFilters.selected.slates = [id];
				leaderboardFilters.selected.matches = s.matches.map((m: any) => m.id);
			}

			// First upcoming slate
			if (now < openAt && (!firstUpcomingSlate || s.order < firstUpcomingSlate.order)) {
				firstUpcomingSlate = s;
			}
		});

		const activeFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.Active];
		const leaderboardFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.Leaderboard];
		const myPicksFilters = newSlateFiltersState.tabs[MARKET_SECTIONS.MyPicks];
		if (!slateFiltersState?.isInited) {
			expandSelectionToGroup(activeFilters, filteredByRuleSlates);
			expandSelectionToGroup(leaderboardFilters, filteredByRuleSlates);

			for (const slateId of activeFilters.selected?.slates ?? []) {
				if (!activeFilters.all.slates.some((s: any) => s.id === slateId)) {
					const slate = filteredByRuleSlates.find((s: any) => s.id === slateId);
					if (slate) activeFilters.all.slates.push(cloneObject(slate));
				}
			}

			for (const slateId of leaderboardFilters.selected?.slates ?? []) {
				if (!leaderboardFilters.all.slates.some((s: any) => s.id === slateId)) {
					const slate = filteredByRuleSlates.find((s: any) => s.id === slateId);
					if (slate) leaderboardFilters.all.slates.push(cloneObject(slate));
				}
			}
		}

		// Expand MyPicks grouped slates selection and add missing slates to all.slates
		expandSelectionToGroup(myPicksFilters, filteredByRuleSlates);
		for (const slateId of myPicksFilters.selected?.slates ?? []) {
			if (!myPicksFilters.all.slates.some((s: any) => s.id === slateId)) {
				const slate = filteredByRuleSlates.find((s: any) => s.id === slateId);
				if (slate) myPicksFilters.all.slates.push(cloneObject(slate));
			}
		}

		[MARKET_SECTIONS.Active, MARKET_SECTIONS.Leaderboard].forEach((section) => {
			const tab = newSlateFiltersState.tabs[section];
			let tabSlates = tab?.all?.slates ?? [];

			// If this section has no slates yet, fall back to all available slates for date-based selection
			if (tabSlates.length === 0 && filteredByRuleSlates.length > 0) {
				tabSlates = filteredByRuleSlates;
			}

			const currentByTime = tabSlates.reduce((best: any, s: any) => {
				const openAt = new Date(s.opening_date);
				const closeAt = new Date(s.closing_date);
				const isActiveByTime = now >= openAt && now <= closeAt;

				if (!isActiveByTime) return best;
				if (!best || (s.order ?? 0) < (best.order ?? 0)) return s;
				return best;
			}, null);

			const currentWithin24hAfterClose = tabSlates.reduce((best: any, s: any) => {
				const closeAt = new Date(s.closing_date);
				const msAfterClose = now.getTime() - closeAt.getTime();
				const isWithin24h = msAfterClose >= 0 && msAfterClose <= 24 * 60 * 60 * 1000;

				if (!isWithin24h) return best;
				if (!best || (s.order ?? 0) < (best.order ?? 0)) return s;
				return best;
			}, null);

			const firstUpcomingFromTab = tabSlates.reduce((best: any, s: any) => {
				const openAt = new Date(s.opening_date);
				if (now < openAt && (!best || (s.order ?? 0) < (best.order ?? 0))) return s;
				return best;
			}, null);

			const lastActiveSlate = tabSlates.reduce((max: any, current: any) => {
				if (!current.closing_date) return max;
				if (!max) return current;
				return new Date(current.closing_date) > new Date(max.closing_date) ? current : max;
			}, null);

			const defaultSlate =
				currentByTime ||
				currentWithin24hAfterClose ||
				firstUpcomingFromTab ||
				lastActiveSlate ||
				null;

			const hasSelection = (tab?.selected?.slates?.length ?? 0) > 0;

			if (!newSlateFiltersState.isInited && !tab.currentSlate && !hasSelection && defaultSlate) {
				tab.currentSlate = defaultSlate;
				tab.selected = tab.selected || { slates: [], matches: [] };
				tab.selected.slates = [defaultSlate.id];
				tab.selected.matches = (defaultSlate.matches ?? []).map((m: any) => m.id);

				expandSelectionToGroup(tab, filteredByRuleSlates);
			}
		});

		newSlateFiltersState.tabs[MARKET_SECTIONS.Active].isSingleSlateFilter =
			data?.lobbyResponse?.slate_trades_limit > 0;

		const activeSlatesIds = newSlateFiltersState.tabs[MARKET_SECTIONS.Active].all.slates.map(
			(s: any) => s.id
		);

		const completed24HoursSlates = filteredByRuleSlates.filter((slate: any) => {
			if (activeSlatesIds.includes(slate.id)) return false;

			const closingDate = new Date(slate.closing_date);
			const timeDiff = now.getTime() - closingDate.getTime();
			const hoursDiff = timeDiff / (1000 * 60 * 60);
			return hoursDiff <= 24 && hoursDiff >= 0;
		});

		newSlateFiltersState.tabs[MARKET_SECTIONS.Active].all.slates = [
			...newSlateFiltersState.tabs[MARKET_SECTIONS.Active].all.slates,
			...completed24HoursSlates,
		].sort((a: any, b: any) => (a?.order || 0) - (b?.order || 0));

		newSlateFiltersState.isInited = true;
		setSlateFiltersState(newSlateFiltersState);
	}, [filteredByRuleSlates, markets, user?.id, userTrades]);

	// PCL: auto-switch slate AFTER filters are fully united
	useEffect(() => {
		// We only care when slate filters are ready
		if (!slateFiltersState.isInited) return;
		if (eventResponse?.competition_type !== EventCompetitionType) return;
		if (!filteredByRuleSlates?.length) return;
		if (!markets.length) return;

		applyPCLAutoSlateSelection();
	}, [
		slateFiltersState.isInited,
		filteredByRuleSlates,
		markets,
		eventResponse?.competition_type,
		applyPCLAutoSlateSelection,
	]);

	const matchToSlateIds = useMemo(() => {
		const map = new Map();
		(slatesData || []).forEach((slate: any) => {
			(slate.matches || []).forEach((m: any) => {
				const arr = map.get(m.id) || [];
				arr.push(slate.id);
				map.set(m.id, arr);
			});
		});
		return map;
	}, [slatesData]);

	const slateUserTradeCounts = useMemo(() => {
		const counts: any = {};
		if (!markets || !userTrades || !matchToSlateIds) return counts;
		userTrades.forEach((t: any) => {
			const market = markets.find((m: any) => m.id === t.market_id);
			if (!market) return;
			const slateIds = matchToSlateIds.get(market.match_id) || [];
			slateIds.forEach((sid: any) => {
				if (!counts[sid]) {
					counts[sid] = {
						total: 0,
						wins: 0,
					};
				}
				counts[sid].total += 1;
				if (t.status === TradeStatus.Win) {
					counts[sid].wins += 1;
				}
			});
		});
		return counts;
	}, [markets, userTrades, matchToSlateIds]);

	const selectedSlateIds = slateFiltersState?.tabs?.[activeMarketSection]?.selected?.slates || [];
	const allSlates = slateFiltersState?.tabs?.[activeMarketSection]?.all?.slates || [];

	const selectedSlate = allSlates?.find((sl: any) => sl.id === selectedSlateIds[0]);

	const selectedGroupName = useMemo(() => {
		if (!selectedSlateIds.length) return null;
		const first = allSlates?.find((s: any) => s.id === selectedSlateIds[0]);
		if (!first?.group_name) return null;
		return selectedSlateIds.every(
			(id: any) => allSlates?.find((s: any) => s.id === id)?.group_name === first.group_name
		)
			? first.group_name
			: null;
	}, [selectedSlateIds, allSlates]);

	const currentSlateMatchIds = useMemo(() => {
		if (!selectedSlate?.matches?.length) return new Set();
		return new Set(selectedSlate.matches.map((m: any) => m.id));
	}, [selectedSlate?.id, selectedSlate?.matches]);

	const currentSlateTotalMarketsCount = useMemo(
		() =>
			getSlateMarketsCount({
				markets,
				currentSlateMatchIds,
				isRemaining: false,
			}),
		[markets, currentSlateMatchIds]
	);

	const currentSlateRemainingMarketsCount = useMemo(
		() =>
			getSlateMarketsCount({
				markets,
				currentSlateMatchIds,
				userTrades,
				isRemaining: true,
			}),
		[markets, currentSlateMatchIds, userTrades]
	);

	const slateTradeLimit = Number.isFinite(lobbyResponse?.slate_trades_limit)
		? Number(lobbyResponse.slate_trades_limit)
		: null;

	const currentSlateTradesLimit =
		selectedGroupName && slateTradeLimit
			? slateTradeLimit * selectedSlateIds.length
			: slateTradeLimit;

	const currentSlateId = selectedSlate?.id;

	const currentSlateName = selectedGroupName || selectedSlate?.name;

	const currentSlateTradesCount = selectedGroupName
		? selectedSlateIds.reduce(
				(sum: number, id: any) => sum + (slateUserTradeCounts?.[id]?.total ?? 0),
				0
			)
		: (slateUserTradeCounts?.[currentSlateId]?.total ?? 0);

	return {
		matchToSlateIds,
		slateUserTradeCounts,
		selectedSlateIds,
		allSlates,
		selectedSlate,
		selectedGroupName,
		currentSlateMatchIds,
		currentSlateTotalMarketsCount,
		currentSlateRemainingMarketsCount,
		slateTradeLimit,
		currentSlateTradesLimit,
		currentSlateId,
		currentSlateName,
		currentSlateTradesCount,
	};
}
